import React from 'react';
import classNames from 'classnames';
import { sharedDragController } from 'javascripts/dnd/controllers/DragController';
import { useController, ApplicationView as view } from '@aha-app/mvc';
import VoidDropTarget from 'javascripts/dnd/components/VoidDropTarget';
import ContainerDropTarget from 'javascripts/dnd/components/ContainerDropTarget';
import CardFieldDropHandler from '../dnd/CardFieldDropHandler';
import CardPreviewRow from './CardPreviewRow';
import CardPreviewHeader from './CardPreviewHeader';
import CardSettings from './CardSettings';
import coverImageExample from '../images/card-cover-image-example.jpg';
import styles from './styles.mod.less';
import AhaCardWCResolver, {
  type AhaCardWCResolverProps,
} from '../../common/cards/AhaCardWCResolver';
import ItemList from 'javascripts/dnd/components/ItemList';
import UndraggableItem from 'javascripts/dnd/UndraggableItem';
import CardRowDropHandler from '../dnd/CardRowDropHandler';
import type CardLayoutEditorController from '../controllers/CardLayoutEditorController';
import DraggableDividerRow from '../dnd/DraggableDividerRow';
import { type CardRow } from 'components/framework/models';

const listKey = 'CardPreview';

const CardPreview = () => {
  const controller = useController<CardLayoutEditorController>();
  const { currentCardView } = controller;
  const { dragging } = sharedDragController.state;
  const cardProps: AhaCardWCResolverProps = {};
  const { cardStyling } = currentCardView;
  if (cardStyling?.styling) {
    const { highlightColor, useBackgroundColor } =
      cardStyling.styling.exampleCardStyling();
    cardProps['highlight-color'] = highlightColor;
    if (useBackgroundColor) {
      cardProps['use-background-color'] = true;
    }
  }

  const dropHandler = new CardRowDropHandler({ controller, listKey });

  return (
    <VoidDropTarget
      accepts={CardFieldDropHandler.accepts}
      className={styles.mainContents}
    >
      <CardSettings />
      <div className={styles.cardCanvas}>
        <div>
          <div
            className={classNames(styles.cardPreview, {
              [styles.dragging]: dragging,
            })}
          >
            <ContainerDropTarget accept={CardFieldDropHandler.accepts}>
              <AhaCardWCResolver {...cardProps}>
                {currentCardView.showParentRecord && <CardPreviewHeader />}
                {currentCardView.coverImages && (
                  <img src={coverImageExample} slot='image' />
                )}
                <ItemList
                  items={currentCardView.rows}
                  listKey={listKey}
                  createDraggableItem={(item: CardRow) =>
                    item.divider
                      ? new DraggableDividerRow({ item, controller })
                      : new UndraggableItem({ item, controller })
                  }
                  createDropHandler={() => dropHandler}
                  ItemComponent={CardPreviewRow}
                />
              </AhaCardWCResolver>
            </ContainerDropTarget>
          </div>
        </div>
      </div>
      <p className='text-muted'>
        Drag and drop the fields onto the card to customize the view.{' '}
        <aha-button
          disabled={currentCardView.inherited || null}
          kind='link'
          size='small'
          onClick={() => controller.actionResetToDefault()}
        >
          Reset to default
        </aha-button>
      </p>
    </VoidDropTarget>
  );
};

export default view(CardPreview);
